import { onlineManager } from "@tanstack/react-query";

/**
 * NetworkPoller is a domain-focused utility that periodically checks network connectivity status,
 * addressing cases where some Android devices do not reliably trigger online/offline events.
 *
 * It polls the `/health` endpoint with a HEAD request to determine connectivity. If the request
 * succeeds, it updates the `onlineManager` to online; otherwise, it marks the application as offline
 * and continues polling every 10 seconds until connectivity is restored.
 *
 * Note: The poll considers both the browser's `navigator.onLine` property and the result of the
 * `/health` endpoint. If `window` is undefined or `navigator.onLine` is false, it will not attempt
 * the network request and will treat the application as offline.
 *
 * Usage:
 * - Call `startPolling()` to begin polling. Only one polling loop runs at a time.
 * - Use the exported singleton `networkPoller` for consistent domain behavior.
 */
class NetworkPoller {
  private isPolling: boolean = false;

  private async pollNetwork() {
    this.isPolling = true;

    if (typeof window !== "undefined" && !window.navigator.onLine) {
      this.isPolling = false;
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 seconds timeout

    const response = await fetch("/health", {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    })
      .catch(() => null)
      .finally(() => {
        clearTimeout(timeoutId);
      });

    if (response?.status) {
      onlineManager.setOnline(true);
      this.isPolling = false;
    } else {
      onlineManager.setOnline(false);
      setTimeout(() => this.pollNetwork(), 10_000);
    }
  }

  async startPolling() {
    if (this.isPolling) {
      return;
    }
    await this.pollNetwork();
  }
}

export const networkPoller = new NetworkPoller();
